"use strict";
/**
 * Domain enums shared between API and web.
 * Source of truth: Figma board "Social Media Management Tool — Conceptual Design Document".
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.MediaProvider = exports.AssetGenerator = exports.CalendarItemKind = exports.ApproverScope = exports.ApprovalType = exports.IndustryPack = exports.SourcePullStatus = exports.FetchFrequency = exports.SourceHealth = exports.SourceType = exports.ApprovalState = exports.SocialPlatform = exports.ContentChannel = exports.ContentKind = exports.ReviewerApproverMode = exports.GlobalApprovalMode = exports.ApproverKind = exports.WorkspaceRole = exports.Role = void 0;
/**
 * LEGACY capability roles — retained during the roles overhaul for backward-compat while
 * callers migrate to {@link WorkspaceRole} + the approver matrix. See
 * docs/roles-approval-overhaul-design.md.
 */
var Role;
(function (Role) {
    Role["CREATOR"] = "CREATOR";
    Role["REVIEWER"] = "REVIEWER";
    Role["STAKEHOLDER"] = "STAKEHOLDER";
    Role["COMPLIANCE_REVIEWER"] = "COMPLIANCE_REVIEWER";
    Role["PUBLISHER"] = "PUBLISHER";
    Role["TENANT_ADMIN"] = "TENANT_ADMIN";
})(Role || (exports.Role = Role = {}));
/**
 * Per-workspace access role (TenantMembership.role). Collapses the legacy capability roles:
 * WORKSPACE_ADMIN = full read/edit of the workspace (Edit Brand Workspace + members + approver
 * matrix); MEMBER = read-only on Edit Brand Workspace, otherwise unrestricted content ops
 * (release gated only by the approval flow). Install-wide admins are {@link User.isTenantAdmin}
 * and act as WORKSPACE_ADMIN everywhere.
 */
var WorkspaceRole;
(function (WorkspaceRole) {
    WorkspaceRole["WORKSPACE_ADMIN"] = "WORKSPACE_ADMIN";
    WorkspaceRole["MEMBER"] = "MEMBER";
})(WorkspaceRole || (exports.WorkspaceRole = WorkspaceRole = {}));
/** Whether an ApproverAssignment is a first-stage Reviewer or the final Approver. */
var ApproverKind;
(function (ApproverKind) {
    ApproverKind["REVIEWER"] = "REVIEWER";
    ApproverKind["APPROVER"] = "APPROVER";
})(ApproverKind || (exports.ApproverKind = ApproverKind = {}));
/**
 * Install-level approval policy (PlatformSettings.globalApprovalMode). CAMPAIGN/POST/NONE
 * override every workspace; PER_WORKSPACE defers to each workspace's own {@link ApprovalType}.
 */
var GlobalApprovalMode;
(function (GlobalApprovalMode) {
    GlobalApprovalMode["CAMPAIGN"] = "CAMPAIGN";
    GlobalApprovalMode["POST"] = "POST";
    GlobalApprovalMode["NONE"] = "NONE";
    GlobalApprovalMode["PER_WORKSPACE"] = "PER_WORKSPACE";
})(GlobalApprovalMode || (exports.GlobalApprovalMode = GlobalApprovalMode = {}));
/**
 * Reviewer flow policy. As an INSTALL-level setting (PlatformSettings.reviewerApproverMode):
 * BOTH / APPROVER_ONLY override every workspace; PER_WORKSPACE defers to each workspace's own
 * value. As a WORKSPACE-level setting (Tenant.reviewerApproverMode) only BOTH / APPROVER_ONLY
 * are meaningful (PER_WORKSPACE is not a valid workspace value and resolves to APPROVER_ONLY).
 */
var ReviewerApproverMode;
(function (ReviewerApproverMode) {
    ReviewerApproverMode["BOTH"] = "BOTH";
    ReviewerApproverMode["APPROVER_ONLY"] = "APPROVER_ONLY";
    /** Install-level only: defer to each Brand Workspace's own reviewer flow. */
    ReviewerApproverMode["PER_WORKSPACE"] = "PER_WORKSPACE";
})(ReviewerApproverMode || (exports.ReviewerApproverMode = ReviewerApproverMode = {}));
/** A single media element of a Post. A Post can hold many, in order. */
var ContentKind;
(function (ContentKind) {
    ContentKind["TEXT"] = "TEXT";
    ContentKind["IMAGE"] = "IMAGE";
    ContentKind["AUDIO"] = "AUDIO";
    ContentKind["VIDEO"] = "VIDEO";
    /**
     * A PDF, which LinkedIn shows as a swipeable carousel of its pages - LinkedIn only (see
     * `maxDocuments` in the social rules). Its own kind rather than an IMAGE or VIDEO carrying a PDF:
     * every image and video path (the JPEG transcode, the gallery cap, the video upload) would
     * misroute one. The row's `text` holds the document's title.
     */
    ContentKind["DOCUMENT"] = "DOCUMENT";
})(ContentKind || (exports.ContentKind = ContentKind = {}));
/** The kind of content ("Post Type"): blog, social, video, or podcast. */
var ContentChannel;
(function (ContentChannel) {
    ContentChannel["BLOG"] = "BLOG";
    ContentChannel["SOCIAL"] = "SOCIAL";
    ContentChannel["VIDEO"] = "VIDEO";
    ContentChannel["PODCAST"] = "PODCAST";
})(ContentChannel || (exports.ContentChannel = ContentChannel = {}));
var SocialPlatform;
(function (SocialPlatform) {
    SocialPlatform["LINKEDIN"] = "LINKEDIN";
    SocialPlatform["FACEBOOK"] = "FACEBOOK";
    SocialPlatform["INSTAGRAM"] = "INSTAGRAM";
    SocialPlatform["THREADS"] = "THREADS";
    SocialPlatform["X"] = "X";
    SocialPlatform["YOUTUBE_SHORTS"] = "YOUTUBE_SHORTS";
})(SocialPlatform || (exports.SocialPlatform = SocialPlatform = {}));
/**
 * Approval state machine:
 * DRAFT → IN_REVIEW → [COMPLIANCE_CHECK] → APPROVED → SCHEDULED → PUBLISHED.
 * "Request changes" moves the item to REVISE (version++); the author edits and
 * re-submits (REVISE → IN_REVIEW). REJECTED is terminal. COMPLIANCE_CHECK only
 * applies to healthcare-pack tenants.
 */
var ApprovalState;
(function (ApprovalState) {
    ApprovalState["DRAFT"] = "DRAFT";
    ApprovalState["IN_REVIEW"] = "IN_REVIEW";
    /** First-stage Reviewer signed off; awaiting the final Approver (two-stage mode only). */
    ApprovalState["REVIEWED"] = "REVIEWED";
    ApprovalState["COMPLIANCE_CHECK"] = "COMPLIANCE_CHECK";
    ApprovalState["APPROVED"] = "APPROVED";
    ApprovalState["SCHEDULED"] = "SCHEDULED";
    ApprovalState["PUBLISHED"] = "PUBLISHED";
    ApprovalState["REJECTED"] = "REJECTED";
    /** Reviewer requested changes — editable, then re-submittable to IN_REVIEW. */
    ApprovalState["REVISE"] = "REVISE";
})(ApprovalState || (exports.ApprovalState = ApprovalState = {}));
var SourceType;
(function (SourceType) {
    SourceType["RSS"] = "RSS";
    SourceType["APIFY"] = "APIFY";
    SourceType["REDDIT"] = "REDDIT";
    SourceType["NEWSLETTER"] = "NEWSLETTER";
})(SourceType || (exports.SourceType = SourceType = {}));
var SourceHealth;
(function (SourceHealth) {
    SourceHealth["HEALTHY"] = "HEALTHY";
    SourceHealth["DEGRADED"] = "DEGRADED";
    SourceHealth["FAILING"] = "FAILING";
    SourceHealth["UNKNOWN"] = "UNKNOWN";
})(SourceHealth || (exports.SourceHealth = SourceHealth = {}));
/** How often a source is fetched (replaces the old free-form refresh interval). */
var FetchFrequency;
(function (FetchFrequency) {
    FetchFrequency["DAILY"] = "DAILY";
    FetchFrequency["WEEKLY"] = "WEEKLY";
    FetchFrequency["BIWEEKLY"] = "BIWEEKLY";
    FetchFrequency["MONTHLY"] = "MONTHLY";
})(FetchFrequency || (exports.FetchFrequency = FetchFrequency = {}));
/** Status of a single source-pull run (history). */
var SourcePullStatus;
(function (SourcePullStatus) {
    SourcePullStatus["PENDING"] = "PENDING";
    SourcePullStatus["RUNNING"] = "RUNNING";
    SourcePullStatus["SUCCEEDED"] = "SUCCEEDED";
    SourcePullStatus["FAILED"] = "FAILED";
})(SourcePullStatus || (exports.SourcePullStatus = SourcePullStatus = {}));
/** Per-industry compliance pack assigned to a tenant. */
var IndustryPack;
(function (IndustryPack) {
    IndustryPack["GENERAL_BUSINESS"] = "GENERAL_BUSINESS";
    IndustryPack["HEALTHCARE"] = "HEALTHCARE";
    IndustryPack["FINANCE"] = "FINANCE";
    IndustryPack["LEGAL"] = "LEGAL";
})(IndustryPack || (exports.IndustryPack = IndustryPack = {}));
/** Workspace approval policy (Tenant.approvalType). */
var ApprovalType;
(function (ApprovalType) {
    /** One approval per campaign; all its posts approve together. */
    ApprovalType["CAMPAIGN"] = "CAMPAIGN";
    /** Each post approves individually; the campaign status aggregates its posts. */
    ApprovalType["POST"] = "POST";
    /** No approval gate. */
    ApprovalType["NONE"] = "NONE";
})(ApprovalType || (exports.ApprovalType = ApprovalType = {}));
/** What an approver assignment authorizes a member to approve. */
var ApproverScope;
(function (ApproverScope) {
    ApproverScope["CAMPAIGN"] = "CAMPAIGN";
    /** Posts on a specific SocialPlatform (scopeValue = platform). */
    ApproverScope["PLATFORM"] = "PLATFORM";
    /** Posts of a specific ContentChannel (scopeValue = channel). */
    ApproverScope["POST_TYPE"] = "POST_TYPE";
})(ApproverScope || (exports.ApproverScope = ApproverScope = {}));
/** What a calendar entry represents. */
var CalendarItemKind;
(function (CalendarItemKind) {
    CalendarItemKind["BLOG_PUBLISH"] = "BLOG_PUBLISH";
    CalendarItemKind["SOCIAL_POST"] = "SOCIAL_POST";
})(CalendarItemKind || (exports.CalendarItemKind = CalendarItemKind = {}));
/** Provenance of a generated/stored visual asset. */
var AssetGenerator;
(function (AssetGenerator) {
    AssetGenerator["NANO_BANANA"] = "NANO_BANANA";
    AssetGenerator["VEO"] = "VEO";
    AssetGenerator["FREEPIK"] = "FREEPIK";
    AssetGenerator["UPLOAD"] = "UPLOAD";
    AssetGenerator["FLUX"] = "FLUX";
    AssetGenerator["MAGNIFIC"] = "MAGNIFIC";
    AssetGenerator["OPENAI"] = "OPENAI";
    AssetGenerator["REPLICATE"] = "REPLICATE";
    AssetGenerator["STABILITY"] = "STABILITY";
    AssetGenerator["FAL"] = "FAL";
    /** Imported from Canva rather than generated. */
    AssetGenerator["CANVA"] = "CANVA";
    /** Imported from Figma rather than generated. */
    AssetGenerator["FIGMA"] = "FIGMA";
})(AssetGenerator || (exports.AssetGenerator = AssetGenerator = {}));
/** External media generators a user/org can bring their own API key for (BYO). */
var MediaProvider;
(function (MediaProvider) {
    MediaProvider["OPENAI"] = "OPENAI";
    MediaProvider["REPLICATE"] = "REPLICATE";
    MediaProvider["STABILITY"] = "STABILITY";
    MediaProvider["FAL"] = "FAL";
    MediaProvider["MAGNIFIC"] = "MAGNIFIC";
})(MediaProvider || (exports.MediaProvider = MediaProvider = {}));
//# sourceMappingURL=enums.js.map